/**
 * A pickup notice property is written to a cart line item at the moment a
 * product is added to cart (see variant-pickup-availability-render.liquid).
 * That value does not update itself afterwards, so if stock changes later
 * (e.g. the shipping store gets restocked, or goes out of stock while the
 * item already sits in the cart), the saved property can become wrong.
 *
 * This script re-checks live stock for any cart line that already has the
 * pickup property, and corrects it:
 * - stock is back        -> property is cleared
 * - message text changed -> property is updated
 * - now needed but empty -> property is filled in
 */
(() => {
  const propertyKey = window.pickupPropertyKey;
  if (!propertyKey) return;

  const PICKUP_SECTION_ID = 'variant-pickup-property';
  let isSyncing = false;

  // Asks Shopify what the pickup message should be for this variant right
  // now, using the same stock logic as the product page. Returns '' when no
  // notice is needed, or null when the check itself failed (e.g., draft/deleted product).
  async function getLivePickupMessage(cartItem) {
    try {
      const productUrl = cartItem.url.split('?')[0];
      const sectionUrl = `${productUrl}?variant=${cartItem.variant_id}&section_id=${PICKUP_SECTION_ID}`;

      const response = await fetch(sectionUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) return null;

      const html = await response.text();
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;

      const propertyInput = wrapper.querySelector('[data-pickup-location-property]');
      return propertyInput ? propertyInput.value.trim() : '';
    } catch (error) {
      console.error('Pickup cart sync: failed to fetch live message', cartItem.variant_id, error);
      return null;
    }
  }

  // Compares the saved property against live stock and updates the cart line
  // if needed. Also cleans up deprecated pickup properties if they exist.
  // Returns true when a change was made.
  async function fixLineItemIfNeeded(cartItem, lineNumber) {
    const deprecatedKeys = [
      'Verzenden niet mogelijk, enkel af te halen te',
      'Pickup availability for',
      'Pickup'
    ].filter(key => key !== propertyKey);

    const foundDeprecatedKeys = deprecatedKeys.filter(key => cartItem.properties?.[key] !== undefined);
    const savedMessage = (cartItem.properties?.[propertyKey] || '').trim();

    // If there is no active property and no deprecated properties, nothing to do
    if (!savedMessage && foundDeprecatedKeys.length === 0) return false;

    // Fetch the live message to determine if the notice should be active
    const liveMessage = await getLivePickupMessage(cartItem);
    // If product fetch failed (null), abort the sync for this item to be safe
    if (liveMessage === null) return false;

    // Determine if anything needs to change
    const activeNeedsUpdate = savedMessage !== liveMessage;
    const hasDeprecatedToClear = foundDeprecatedKeys.length > 0;

    if (!activeNeedsUpdate && !hasDeprecatedToClear) return false;

    // Construct the properties payload
    const updatedProperties = { ...cartItem.properties };
    if (activeNeedsUpdate) {
      updatedProperties[propertyKey] = liveMessage;
    }
    foundDeprecatedKeys.forEach(key => {
      updatedProperties[key] = ''; // setting to empty string deletes the property in Shopify
    });

    const response = await fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        line: lineNumber,
        quantity: cartItem.quantity,
        properties: updatedProperties,
      }),
    });

    return response.ok;
  }

  async function syncPickupProperties() {
    if (isSyncing) return;
    isSyncing = true;

    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      let didChangeCart = false;

      for (let i = 0; i < cart.items.length; i += 1) {
        try {
          const lineNumber = i + 1;
          const wasFixed = await fixLineItemIfNeeded(cart.items[i], lineNumber);
          if (wasFixed) {
            didChangeCart = true;
            break;
          }
        } catch (itemError) {
          // Skip this line item, but keep checking the rest of the cart.
          console.error('Pickup cart sync: could not check line item', i + 1, itemError);
        }
      }

      if (didChangeCart) {
        document.querySelector('cart-items')?.onCartUpdate?.();
        document.querySelector('cart-drawer-items')?.onCartUpdate?.();
      }
    } catch (error) {
      console.error('Pickup cart sync failed', error);
    } finally {
      isSyncing = false;
    }
  }

  document.addEventListener('DOMContentLoaded', syncPickupProperties);
  document.querySelector('#cart-icon-bubble')?.addEventListener('click', syncPickupProperties);

  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, syncPickupProperties);
  }
})();
